/*
 * Created by Anup Shrestha (anup.stha012@gmail.com)
 * Copyright (c) 2022.  All rights reserved.
 * Last modified 5/10/22, 2:38 PM
 *
 */

import { store } from 'stores';
import { ApiResponse } from 'types/api';
import { Member, MemberMedicalHistory } from 'types/MemberTypes';

import { addRole, addUser } from 'features/auth';
import { getUserProfile } from 'features/auth/api/requests/auth.request';
import { setMember, setUser } from 'features/members';
import { Invoice, MemberSubscription, MemberTestList } from 'features/members/types';

import { privateAgent } from 'config/axios';

export type PaginationObject = {
  total: number;
  count: number;
  path: string;
  per_page: number;
  current_page: number;
  total_pages: number;
};

type MemberList = {
  list: Member[];
  pagination: PaginationObject;
};

type OtherFields = {
  id: number;
  name: string;
  slug: string;
  value_type: string;
  required: boolean;
};

export type MemberUpdateBody = {
  name: string;
  lat?: number;
  lng?: number;
  phone: string;
  image?: string;
  address: string;
  email: string;
  dob_ad: number;
  dob_bs?: number;
  province?: string;
  district?: string;
  city?: string;
  gender: 'Male' | 'Female' | 'Others';
  marital_status: 'Single' | 'Married';
};

export const getNestedMemberList = async (
  roleId: number,
  parentId: number,
  pageNumber: number,
  filterParams?: string,
  searchTerms?: string
): Promise<MemberList> => {
  const response = filterParams
    ? await privateAgent.get<ApiResponse<MemberList>>(
        `member/lists/secondaryDetail/filter${filterParams}`
      )
    : searchTerms
    ? await privateAgent.get<ApiResponse<MemberList>>(
        `member/list/${roleId}/${parentId}/search?keyword=${searchTerms}`
      )
    : await privateAgent.get<ApiResponse<MemberList>>(
        `/member/list/${roleId}/${parentId}?page=${pageNumber}`
      );

  return response.data.data;
};

export const postOrgMember = (body: any) => {
  const { district, province, city, address, ...rest } = body;
  return privateAgent.post<ApiResponse<Member>>('user/store', {
    ...rest,
    address: `${city ?? ''}, ${district ?? ''}, ${province ?? ''} - ${address}`,
  });
};

export const postNormalMember = (body: any) => {
  const { district, province, city, address, ...rest } = body;
  return privateAgent.post<ApiResponse<Member>>('member/store', {
    ...rest,
    address: `${city ?? ''}, ${district ?? ''}, ${province ?? ''} - ${address}`,
  });
};

export const getOtherFieldsList = async (): Promise<OtherFields[]> => {
  const response = await privateAgent.get<ApiResponse<OtherFields[]>>('medical_history/category/');
  return response.data.data;
};

export const updateUserProfile = (profileId: number, body: MemberUpdateBody) => {
  const { district, province, city, address, ...rest } = body;

  return new Promise((resolve, reject) => {
    privateAgent
      .post<ApiResponse<Member>>(`member/update/${profileId}`, {
        ...rest,
        address: `${city ?? ''}, ${district ?? ''}, ${province ?? ''} - ${address}`,
      })
      .then((response) => {
        const member = store.getState().members.member;
        const user = store.getState().members.user;
        const loggedInUser = store.getState().auth.user;

        const updatedUser = response.data.data;

        if (loggedInUser?.id === updatedUser.id) {
          getUserProfile().then(() => {
            store.dispatch(addRole(updatedUser.role.slug));
            store.dispatch(addUser(updatedUser));
          });
        } else if (member?.id === updatedUser.id) {
          store.dispatch(setMember(updatedUser));
        } else if (user?.id === response.data.data.id) {
          store.dispatch(setUser(updatedUser));
        }

        resolve(response.data.message);
      })
      .catch((error) => {
        reject(error.response);
      });
  });
};

export const addDetailsToMember = (roleId: number, memberId: number, data: Record<string, any>) => {
  const values = Object.values(data);
  const keys = Object.keys(data);
  const requestBody: any[] = [];
  requestBody.push(
    ...keys.map((element, index) => ({
      detail_category_id: element.split('-')[0],
      value: values[index],
    }))
  );

  return new Promise((resolve, reject) => {
    privateAgent
      .post<any>('member/detail', {
        member_id: memberId,
        role_id: roleId,
        data: requestBody,
      })
      .then((response) => {
        const currentMember = store.getState().members.member;
        const currentUser = store.getState().members.user;
        const loggedInUser = store.getState().auth.user;

        const details = response.data.data;

        if (loggedInUser?.id === memberId) {
          getUserProfile().then(() => {
            store.dispatch(addUser({ ...loggedInUser, details }));
          });
        } else if (currentMember?.id === memberId) {
          store.dispatch(
            setMember({
              ...currentMember,
              details,
            })
          );
        } else if (currentUser?.id === memberId) {
          store.dispatch(
            setUser({
              ...currentUser,
              details,
            })
          );
        }

        resolve(response.data.message);
      })
      .catch((error) => {
        reject(error.response);
      });
  });
};

export const postMedicalHistoryToPatient = (memberId: number, data: any) => {
  console.log(data);
  const values = Object.values(data);
  const keys = Object.keys(data);
  const requestBody: any[] = [];
  requestBody.push(
    ...keys.map((element, index) => {
      return {
        medical_history_category_id: element.split('-')[0],
        value: typeof values[index] === 'number' ? values[index] : null,
        note: typeof values[index] === 'string' ? values[index] : '',
      };
    })
  );

  const groupBy = (objectArray: any, property: any) => {
    return objectArray.reduce(function (acc: any, obj: any) {
      const key = obj[property];
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});
  };
  const mergeObject = (objectA: any, objectB: any) => {
    const res: any = {};
    Object.keys({ ...objectA, ...objectB }).map((key) => {
      res[key] = objectB[key] || objectA[key];
    });
    return res;
  };

  const body = groupBy(requestBody, 'medical_history_category_id');

  const finalBody = Object.values(body).map((element: any) =>
    element.length === 1 ? mergeObject(element[0], {}) : mergeObject(element[0], element[1])
  );
  const finalReqBody = finalBody.map((element) => ({
    ...element,
    value: element.value ? 'Yes' : 'No',
    note: !element.value ? 'N/A' : element.note ? element.note : 'N/A',
  }));

  return new Promise((resolve, reject) => {
    privateAgent
      .post<ApiResponse<MemberMedicalHistory[]>>('medical_history/category/value/', {
        member_id: memberId,
        data: finalReqBody,
      })
      .then((response) => {
        const medical_history = response.data.data;
        const currentMember = store.getState().members.member;

        if (currentMember?.id === memberId) {
          store.dispatch(
            setMember({
              ...currentMember,
              medical_history,
            })
          );
        }

        resolve(response.data.message);
      })
      .catch((error) => {
        reject(error.response);
      });
  });
};

export const getMemberTestList = async (
  memberId: number,
  testCategoryId: number,
  pageNumber = 1
): Promise<MemberTestList> => {
  const response = await privateAgent.get<ApiResponse<MemberTestList>>(
    `test/member?mid=${memberId}&tcid=${testCategoryId}&page=${pageNumber}`
  );

  return response.data.data;
};

export const getMemberTestReportByDate = (
  memberId: number,
  from_time_stamp?: number,
  to_time_stamp?: number
) => {
  if (!to_time_stamp) {
    return privateAgent.get<ApiResponse<MemberTestList>>(
      `test/report?from_time_stamp=${from_time_stamp}&member_id=${memberId}`
    );
  }
  return privateAgent.get<ApiResponse<MemberTestList>>(
    `test/report?from_time_stamp=${from_time_stamp}&to_time_stamp=${to_time_stamp}&member_id=${memberId}`
  );
};

export const getMemberSubscriptionDetails = async (
  member_id: number
): Promise<MemberSubscription> => {
  const response = await privateAgent.get<ApiResponse<MemberSubscription>>(
    `member/subscription/${member_id}`
  );
  return response.data.data;
};

export const getInvoiceList = async (member_id: number): Promise<Invoice[]> => {
  const response = await privateAgent.get<ApiResponse<Invoice[]>>(`invoice/member/${member_id}`);
  return response.data.data;
};
